import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import ComboBox from "@inovua/reactdatagrid-community/packages/ComboBox";
import ScrollContainer from "@inovua/reactdatagrid-community/packages/react-scroll-container-pro/src";

const stopPropagation = event => event.stopPropagation();

const renderListScroller = scrollerProps => (
  <ScrollContainer
    {...scrollerProps}
    applyCSSContainOnScroll={false}
    viewStyle={{ width: "100%" }}
    onWheel={stopPropagation}
  />
);

const normalizeConceptText = text =>
  String(text == null ? "" : text)
    .trim()
    .toLowerCase();

const conceptLabel = concept =>
  concept && concept.label != null ? concept.label : concept && concept.id;

const conceptsMatchingText = (concepts, query) =>
  concepts.filter(concept => {
    const label = normalizeConceptText(conceptLabel(concept));
    const id = normalizeConceptText(concept && concept.id);
    return label.indexOf(query) !== -1 || id.indexOf(query) !== -1;
  });

const resolveTypedConcept = (concepts, typed, activeId) => {
  const query = normalizeConceptText(typed);
  if (!query || !Array.isArray(concepts) || !concepts.length) {
    return null;
  }

  const exact = concepts.find(
    concept =>
      normalizeConceptText(concept.id) === query ||
      normalizeConceptText(conceptLabel(concept)) === query
  );
  if (exact) {
    return exact.id;
  }

  const matches = conceptsMatchingText(concepts, query);
  if (matches.length === 1) {
    return matches[0].id;
  }
  if (matches.length > 1) {
    const highlighted = matches.find(
      concept => String(concept.id) === String(activeId)
    );
    return (highlighted || matches[0]).id;
  }
  return null;
};

const readTypedText = combo => {
  if (!combo) {
    return "";
  }
  const input =
    typeof combo.getTextInputNode === "function"
      ? combo.getTextInputNode()
      : null;
  if (input && typeof input.getValue === "function") {
    const liveValue = input.getValue();
    if (liveValue != null) {
      return liveValue;
    }
  }
  return typeof combo.getText === "function" ? combo.getText() || "" : "";
};

const isInsideConceptEditor = (target, root) => {
  if (!(target instanceof Node)) {
    return false;
  }
  if (root && root.contains(target)) {
    return true;
  }
  return Boolean(
    target.closest && target.closest(".inovua-react-toolkit-combo-box__list")
  );
};

const ConceptSelectEditor = ({
  editorProps,
  nativeScroll,
  value,
  theme,
  rtl,
  cellProps,
  onChange,
  onComplete,
  onCancel,
  onTabNavigation
}) => {
  const rootRef = useRef(null);
  const columnEditorProps = editorProps || {};
  const editorStyle = columnEditorProps.style;

  useEffect(() => {
    const onPointerDown = event => {
      if (isInsideConceptEditor(event.target, rootRef.current)) {
        return;
      }
      if (onComplete) {
        onComplete();
      }
    };

    document.addEventListener("mousedown", onPointerDown, true);
    return () => document.removeEventListener("mousedown", onPointerDown, true);
  }, [onComplete]);

  return (
    <div
      ref={rootRef}
      className="InovuaReactDataGrid__cell__editor InovuaReactDataGrid__cell__editor--select"
    >
      <ComboBox
        {...columnEditorProps}
        collapseOnSelect
        defaultExpanded
        autoFocus
        focusOnClick
        toggleExpandOnClick={false}
        collapseOnBlur={false}
        filterDelay={0}
        theme={columnEditorProps.theme || theme}
        renderListScroller={nativeScroll ? undefined : renderListScroller}
        defaultValue={value}
        rtl={rtl}
        onChange={nextValue => {
          if (onChange) {
            onChange(nextValue);
          }
        }}
        constrainTo=".inovua-react-scroll-container__wrapper"
        style={{
          ...editorStyle,
          minWidth: Math.max(
            0,
            (cellProps && cellProps.computedWidth
              ? cellProps.computedWidth
              : 0) - 30
          )
        }}
        onItemClick={item => {
          if (onChange) {
            onChange(item.id);
          }
          if (onComplete) {
            setTimeout(onComplete, 0);
          }
        }}
        onKeyDown={(event, combo) => {
          const { key } = event;
          if (key === "Escape" && combo && !combo.getExpanded() && onCancel) {
            onCancel(event);
          }
          if (key === "Enter") {
            event.preventDefault();
            const concepts = Array.isArray(columnEditorProps.dataSource)
              ? columnEditorProps.dataSource
              : [];
            const typed = readTypedText(combo);
            const activeId =
              combo && typeof combo.getActiveItem === "function"
                ? combo.getActiveItem()
                : null;
            const conceptId = resolveTypedConcept(concepts, typed, activeId);
            if (conceptId == null) {
              if (onChange) {
                onChange(value);
              }
            } else if (onChange) {
              onChange(conceptId);
            }
            if (onComplete) {
              setTimeout(onComplete, 0);
            }
          }
          if (key === "Tab" && onTabNavigation) {
            event.preventDefault();
            onTabNavigation(true, event.shiftKey ? -1 : 1);
          }
        }}
      />
    </div>
  );
};

ConceptSelectEditor.propTypes = {
  editorProps: PropTypes.shape({
    style: PropTypes.shape({}),
    theme: PropTypes.string
  }),
  nativeScroll: PropTypes.bool,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  theme: PropTypes.string,
  rtl: PropTypes.bool,
  cellProps: PropTypes.shape({
    computedWidth: PropTypes.number
  }),
  onChange: PropTypes.func,
  onComplete: PropTypes.func,
  onCancel: PropTypes.func,
  onTabNavigation: PropTypes.func
};

ConceptSelectEditor.defaultProps = {
  editorProps: {},
  nativeScroll: false,
  value: null,
  theme: undefined,
  rtl: false,
  cellProps: undefined,
  onChange: undefined,
  onComplete: undefined,
  onCancel: undefined,
  onTabNavigation: undefined
};

export default ConceptSelectEditor;
